/**
 * SezioneQuestionarioLead — nella scheda del lead, ciò che arriva dai questionari: la persona di
 * riferimento, da dove è arrivata, il consenso agli aggiornamenti con il suo storico, e il
 * «non contattare» (che si può mettere anche da qui, se la persona lo chiede per telefono).
 */

import React, { useState } from 'react';
import { Alert, Box, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { etichettaProvenienza } from '@shared/questionari';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import { impostaNonContattare } from '../../services/platformCrmService';
import type { PlatformLead } from '../../types/platformCrm';
import { CANALE_LABELS, CONSENSO_LABELS, CONSENSO_TONES, formatDate, formatDateTime, nomeLingua } from './labels';

const COME_LABELS: Record<string, string> = {
  modulo: 'dal modulo',
  email_conferma: "dal bottone nell'email",
  email_uscita: "dal collegamento «esci» nell'email",
  console: 'dalla console',
};

const Riga: React.FC<{ nome: string; children: React.ReactNode }> = ({ nome, children }) => (
  <Stack direction="row" spacing={1.5} alignItems="baseline">
    <Typography variant="caption" sx={{ color: tokens.color.ash, fontWeight: 600, width: 120, flexShrink: 0 }}>{nome}</Typography>
    <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
  </Stack>
);

export const SezioneQuestionarioLead: React.FC<{
  lead: PlatformLead;
  /** La lingua dell'ultima risposta, se c'è. */
  lingua?: string | null;
  onCambiato: () => void;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}> = ({ lead, lingua, onCambiato, showSnack }) => {
  const [salvando, setSalvando] = useState(false);
  const consenso = lead.consenso_aggiornamenti;
  const stato = consenso?.stato ?? 'nessuno';
  const provenienza = lead.provenienza;
  const haDati = !!(lead.referente_nome || lead.referente_ruolo || provenienza || consenso || lead.non_contattare || lingua);

  const cambiaNonContattare = async (valore: boolean) => {
    setSalvando(true);
    try {
      await impostaNonContattare(lead, valore);
      showSnack(valore ? 'Segnato: non contattare' : 'Tolto il «non contattare»', 'success');
      onCambiato();
    } catch (e) {
      console.error('[platform-crm] aggiornamento «non contattare» fallito', e);
      showSnack(e instanceof Error ? e.message : 'Aggiornamento non riuscito', 'error');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1" fontWeight={700}>Dai questionari</Typography>
      {!haDati && (
        <Typography variant="body2" sx={{ color: tokens.color.mist }}>Nessun dato da un questionario.</Typography>
      )}
      {(lead.referente_nome || lead.referente_ruolo) && (
        <Riga nome="Referente">
          <Typography variant="body2">{[lead.referente_nome, lead.referente_ruolo].filter(Boolean).join(' · ')}</Typography>
        </Riga>
      )}
      {provenienza && (
        <Riga nome="Arrivato da">
          <Typography variant="body2">
            {etichettaProvenienza(provenienza)}
            {[provenienza.utm_source, provenienza.utm_medium, provenienza.utm_campaign].some(Boolean) && (
              <Box component="span" sx={{ color: tokens.color.ash }}>
                {' '}({[provenienza.utm_source, provenienza.utm_medium, provenienza.utm_campaign].filter(Boolean).join(' / ')})
              </Box>
            )}
          </Typography>
        </Riga>
      )}
      {lingua && <Riga nome="Lingua"><Typography variant="body2">{nomeLingua(lingua)}</Typography></Riga>}
      <Riga nome="Aggiornamenti">
        <Stack spacing={0.5}>
          <Box><SoftChip label={CONSENSO_LABELS[stato]} tone={CONSENSO_TONES[stato]} /></Box>
          {consenso && (
            <>
              <Typography variant="caption" sx={{ color: tokens.color.ash }}>
                Chiesto il {formatDateTime(consenso.richiesto_at)} · versione {consenso.versione} · {consenso.canali.map((c) => CANALE_LABELS[c]).join(' e ')}
                {consenso.confermato_at ? ` · confermato il ${formatDateTime(consenso.confermato_at)}` : ''}
                {consenso.revocato_at ? ` · revocato il ${formatDateTime(consenso.revocato_at)}` : ''}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: tokens.color.graphite }}>«{consenso.testo}»</Typography>
              {(consenso.storico ?? []).length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {consenso.storico.map((v, i) => (
                    <Typography component="li" variant="caption" key={`${v.il}-${i}`} sx={{ color: tokens.color.ash }}>
                      {formatDateTime(v.il)}: {CONSENSO_LABELS[v.stato].toLowerCase()} {COME_LABELS[v.come] ?? v.come}
                      {v.versione ? ` (versione ${v.versione})` : ''}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </Stack>
      </Riga>
      <Riga nome="Non contattare">
        <Stack spacing={0.5}>
          <FormControlLabel
            control={<Switch size="small" checked={lead.non_contattare === true} disabled={salvando} onChange={(e) => cambiaNonContattare(e.target.checked)} />}
            label={(
              <Typography variant="body2">
                {lead.non_contattare ? `Sì, dal ${formatDate(lead.non_contattare_at)}` : 'No'}
              </Typography>
            )}
          />
          {lead.non_contattare && (
            <Alert severity="warning" sx={{ py: 0 }}>
              Ha chiesto di non essere contattato: niente email, telefonate o messaggi. Togliere il segno non ridà il consenso agli aggiornamenti.
            </Alert>
          )}
        </Stack>
      </Riga>
    </Stack>
  );
};
